<template>
  <not-found
    v-if="notFound && !loading && !languageIsChanging"
    :context="appState.sitecoreContext"
  />
  <!-- <route-loading v-else-if="loading && !$eq(route.query.referrer, 'app')" /> -->
  <layout v-if="!loading" :route="appState.routeData" :context="appState.sitecoreContext" />
</template>

<script>
import { isEditorActive } from '@sitecore-jss/sitecore-jss-vue';
import { layoutServiceFactory } from './lib/layout-service-factory';
import config from './temp/config';
import axios from 'axios';

import Layout from './Layout';
import NotFound from './NotFound';
// import RouteLoading from './RouteLoading';
import { canUseDOM } from '@/utils/dom-utils';
import { gtmPush } from '@/utils/gtm-utils';
import { formatCarPrice } from '@/utils/site-utils';
import { APP_CONST } from '@/utils/constants';
import useAppStore from '@/store/appStore';
import { notifyRouteContentReady } from '@/utils/route-scroll-utils';
// Dynamic route handler for Sitecore items.
// Because JSS app routes are defined in Sitecore, traditional static routing isn't enough -
// we need to load dynamic route data from Sitecore when the client side route changes.
// So vue-router delegates all route rendering to this handler, which attempts to get the right
// route data from Sitecore - and if none exists, renders the not found component.
export default {
  name: 'Route-Handler',
  data() {
    const state = { notFound: true, defaultLanguage: config.defaultLanguage, loading: true };

    // 记录当前已解析（成功返回 route 数据）的 Sitecore 路径，
    // 用于识别伪静态分页 URL 中的筛选段导航（如 /webinars/discovery/page1），
    // 筛选段不是真实 Sitecore 路由，无需重新请求 Layout Service
    const initSegments = this.route?.params?.sitecoreRoute;
    const initPath =
      Array.isArray(initSegments) && initSegments.length ? `/${initSegments.join('/')}` : '/';
    state.resolvedRoutePath = initPath.toLowerCase().replace(/\/+$/, '') || '/';

    // To take advantage of Vue's reactive data for tracking app state changes, we need
    // to reference the same `state` object that the $jss store references in order for mutations to be observed.
    // $jss is attached to the App instance via `SitecoreJssPlugin`.
    const appState = this.$jss.store.state;
    // if the app state has routeData, we don't need to load it and don't need a loading screen
    if (appState.routeData) {
      state.loading = false;
    }

    // route path from vue router - if route was resolved, it's not a 404
    if (this.route !== null) {
      state.notFound = false;
    }

    // if we have an initial SSR state, and that state doesn't have a valid route data,
    // then this is a 404 route.
    if (!appState.routeData) {
      state.notFound = true;
    }

    // if we have initial context data, and that context data has a language defined, set the default language
    // (this makes the language of content follow the Sitecore context language cookie)
    // note that a route-based language (i.e. /de-DE) will override this default; this is for home.
    if (appState.sitecoreContext && appState.sitecoreContext.language) {
      state.defaultLanguage = appState.sitecoreContext.language;
    }

    return { ...state, appState };
  },
  props: {
    route: {
      type: Object,
    },
  },
  created() {
    // if no existing routeData is present (from SSR), get Layout Service fetching the route data
    if (!this.appState.routeData) {
      this.updateRouteData();
    }
    // tell app to sync its current language with the route language
    this.updateLanguage();
  },
  inject: {
    languageIsChanging: {
      type: Boolean,
    },
    changeAppLanguage: {
      type: Function,
    },
    showGlobalLoading: {
      type: Function,
      default: () => {},
    },
    hideGlobalLoading: {
      type: Function,
      default: () => {},
    },
  },
  methods: {
    /**
     * Loads route data from Sitecore Layout Service into appState.routeData
     */
    updateRouteData() {
      this.showGlobalLoading();
      const routeFullPath = this.route.fullPath;
      let sitecoreRoutePath = this.route.params.sitecoreRoute
        ? this.route.params.sitecoreRoute.join('/')
        : '/';
      if (!sitecoreRoutePath.startsWith('/')) {
        sitecoreRoutePath = `/${sitecoreRoutePath}`;
      }

      const language =
        this.route.params.lang || this.appState.sitecoreContext.language || this.defaultLanguage;
      this.loading = true;

      axios.defaults.withCredentials = false;
      // instantiate layout service
      const layoutServiceInstance = layoutServiceFactory.create();

      // 伪静态分页 URL 可能带有筛选段（如 /webinars/discovery/page2），
      // 筛选段不是真实 Sitecore 路由，404 时逐段去掉末尾路径段重试
      const allowFallback = this.route.params.page !== undefined;
      const fetchWithFallback = (path) => {
        return layoutServiceInstance.fetchLayoutData(path, language).then((routeData) => {
          const found = routeData !== null && routeData.sitecore && routeData.sitecore.route;
          const stripped = path.replace(/\/[^/]+$/, '');
          if (!found && allowFallback && stripped && stripped !== path) {
            return fetchWithFallback(stripped);
          }
          return { routeData, resolvedPath: path };
        });
      };

      // get the route data for the new route
      fetchWithFallback(sitecoreRoutePath).then(({ routeData, resolvedPath }) => {
        if (routeData !== null && routeData.sitecore.route) {
          this.resolvedRoutePath = resolvedPath.toLowerCase();
          // Update the JSS store instance with the fetched data.
          // This will signal the RouteHandler to update/re-render, as well as any components
          // that are referencing the JSS store instance in their `data` object.
          formatCarPrice(routeData.sitecore.route.placeholders, APP_CONST.carPriceList);
          this.$jss.store.setSitecoreData(routeData);
          // 从 Sitecore 页面数据中获取语言设置
          const pageLanguage = routeData.sitecore.route.fields?.language?.value || language;
          if (canUseDOM()) {
            const appStore = useAppStore();
            appStore.setLang(pageLanguage);
          }
          this.notFound = false;
        } else {
          this.$jss.store.setSitecoreData(routeData);

          // 兜底逻辑：如果是自动跳转过来的（autoRedirect=1），且页面不存在，
          // 则跳转到目标国家的首页，避免展示 404 页面
          if (this.route.query.autoRedirect === '1') {
            const pathSegments = this.route.path.split('/');
            const countryPrefix = pathSegments[1] || 'en'; // 提取语言/国家前缀，默认 en

            // 使用 replace 替换当前路由，清除 autoRedirect 标记
            this.$router.replace({
              path: `/${countryPrefix}`,
              query: {},
              hash: '',
            });
            return; // 提前返回，不再执行后续 loading 状态更新
          }

          this.notFound = true;
        }
        this.loading = false;
        this.hideGlobalLoading();

        if (canUseDOM()) {
          this.$nextTick(() => notifyRouteContentReady(routeFullPath));
        }

        if (canUseDOM() && this.route.hash) {
          const targetId = this.route.hash.replace('#', '');
          this.$nextTick(() => {
            const targetEl = document.getElementById(targetId);
            if (targetEl) {
              const offset = targetEl.getBoundingClientRect();
              window.scrollTo({
                top: window.scrollY + offset.top - 80,
                behavior: 'smooth',
              });
            }
          });
        }
      });
    },
    /**
     * Updates the current app language to match the route data.
     */
    updateLanguage() {
      const newLanguage =
        this.route.params.lang || this.appState.sitecoreContext.language || this.defaultLanguage;
      // `changeAppLanguage` is "inject"-ed from AppRoot
      this.changeAppLanguage(newLanguage);
    },
  },
  mounted() {
    // 处理页面首次加载时 URL 中的 hash 锚点定位
    // SSR 渲染时 routeData 已存在，不会触发 updateRouteData() 和 route watcher，
    // 导致 hash 锚点未被处理（如 SiteButton 的 contactId 跳转）
    if (canUseDOM() && this.route.hash) {
      const targetId = this.route.hash.replace('#', '');
      this.$nextTick(() => {
        const targetEl = document.getElementById(targetId);
        if (targetEl) {
          const offset = targetEl.getBoundingClientRect();
          window.scrollTo({
            top: window.scrollY + offset.top - 80,
            behavior: 'smooth',
          });
        }
      });
    }
  },
  watch: {
    // watch for a change in the 'route' prop
    route(newRoute, oldRoute) {
      // if the route contains a hash value, assume the URL is a named anchor/bookmark link, e.g. /page#anchorId.
      // in that scenario, we don't want to fetch new route data but instead allow default browser behavior.
      if (newRoute.hash !== '' && newRoute.path === oldRoute.path) {
        if (canUseDOM()) {
          const targetId = newRoute.hash.replace('#', '');
          this.$nextTick(() => {
            const targetEl = document.getElementById(targetId);
            if (targetEl) {
              const offset = targetEl.getBoundingClientRect();
              window.scrollTo({
                top: window.scrollY + offset.top - 80,
                behavior: 'smooth',
              });
            }
          });
        }
        return;
      }
      const { fullPath } = newRoute;
      // 注释掉强制重定向到带语言前缀的URL的逻辑
      // const reg = new RegExp(`^/(en|[A-Z]{2}|[a-z]{2})(/|$)`, 'gi');
      // if (!reg.test(fullPath)) {
      //   window.location.replace(`${config.sitecoreApiHost}/en${fullPath}`);
      // }
      // if in Sitecore editor - force reload instead of route data update
      // avoids confusing Sitecore's editing JS
      if (isEditorActive()) {
        window.location.assign(newRoute.path);
        return;
      }

      // 仅页码变化时（如 /page2 -> /page3），sitecoreRoute 与语言不变，
      // 无需重新请求 Layout Service，交给页面组件自行处理分页
      const newSitecoreRoute = newRoute.params.sitecoreRoute
        ? newRoute.params.sitecoreRoute.join('/')
        : '';
      const oldSitecoreRoute = oldRoute.params.sitecoreRoute
        ? oldRoute.params.sitecoreRoute.join('/')
        : '';
      const langUnchanged = newRoute.params.lang === oldRoute.params.lang;
      if (newSitecoreRoute === oldSitecoreRoute && langUnchanged) {
        return;
      }

      // 伪静态分页的筛选导航（如 /webinars/page1 → /webinars/discovery/page1）：
      // 目标仍是当前已解析的页面时跳过 Layout Service 请求，由列表组件自行处理筛选
      if (langUnchanged && this.resolvedRoutePath) {
        const newBasePath = `/${newSitecoreRoute}`.toLowerCase().replace(/\/+$/, '') || '/';
        // 回到当前列表页本身（如筛选改选 all/清空 → /webinars/page1）
        if (newBasePath === this.resolvedRoutePath) {
          return;
        }
        // 列表组件发起的纯筛选导航（带 filterOnlyNavigation 标记），仅末尾筛选段不同
        const isFilterOnlyNav = !!(canUseDOM() && window.history.state?.filterOnlyNavigation);
        if (
          isFilterOnlyNav &&
          (newBasePath.replace(/\/[^/]+$/, '') || '/') === this.resolvedRoutePath
        ) {
          return;
        }
      }

      this.updateLanguage();
      this.updateRouteData();
    },
  },
  components: {
    Layout,
    NotFound,
    // RouteLoading
  },
};
</script>
